import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Stack,
  Button,
  InputLabel,
  Tooltip,
  FormHelperText,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { setWeight } from "redux/slices/scoringSlice";
import { useDispatch } from "react-redux";
import { Controller } from "react-hook-form";
const evaluationOptions = [
  "Pass",
  "Fail",
  "Neutral",
]


const ScoreRangeComponent = ({ item, type, control, handleRangeSet, handleChange }) => {
  const dispatch = useDispatch();

  const [ranges, setRanges] = useState([
    {
      lower: item?.range?.lowerBound?.option || "00:00:00",
      upper: item?.range?.upperBound?.option || "00:00:00",
      lowerScore: item?.range?.lowerBound?.score || 2,
      upperScore: item?.range?.upperBound?.score || 3,
    },
  ]);
  const [between, setBetween] = useState(
    item?.range?.between?.map((e) => {
      return {
        lower: e?.option?.split("-")[0] || "00:00:00",
        upper: e?.option?.split("-")[1] || "00:00:00",
        score: e?.score,
      };
    }) || []
  );
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const validateTimeInput = (value) => {
    const regex = /^([0-1]?[0-9]|2[0-3])?(:[0-5]?[0-5]?[0-9])?(:[0-5]?[0-9]?)?$/;
    return regex.test(value) ? value : "00:00:00";
  };

  const validateScoreInput = (value) => {
    return !isNaN(value) && value >= 0 ? value : 0;
  };

  const handleRangeChange = (index, field, value) => {
    const updatedRanges = [...ranges];
    if (field === "lower" || field === "upper") {
      updatedRanges[index][field] = validateTimeInput(value);
      if (field === "lower") {
        updatedRanges[index].upper = updatedRanges[index].lower;
      }
    } else if (field === "lowerScore") {
      updatedRanges[index].lowerScore = validateScoreInput(value);
    } else if (field === "upperScore") {
      updatedRanges[index].upperScore = validateScoreInput(value);
    }
    setRanges(updatedRanges);
  };

  const handleBetweenChange = (id, field, value) => {
    const updatedBetween = between.map((item) =>
      item.id === id
        ? {
          ...item,
          [field]:
            field === "lower" || field === "upper"
              ? validateTimeInput(value)
              : validateScoreInput(value),
        }
        : item
    );
    setBetween(updatedBetween);
  };

  const handleAddBetween = () => {
    setError("");
    setEditing(false);
    const lastRangeUpper = ranges[0].upper;
    setBetween([
      ...between,
      {
        id: Date.now(),
        lower: lastRangeUpper,
        upper: incrementTime(lastRangeUpper),
        score: 0,
      },
    ]);
  };

  const handleRemoveBetween = (id) => {
    setBetween(between.filter((item) => item.id !== id));
  };

  const incrementTime = (time) => {
    const [hours, minutes, seconds] = time.split(":").map(Number);

    // Increment seconds
    let newSeconds = seconds + 1;
    let newMinutes = minutes;
    let newHours = hours;

    // Handle overflow of seconds
    if (newSeconds >= 60) {
      newSeconds = 0;
      newMinutes += 1;
    }

    // Handle overflow of minutes
    if (newMinutes >= 60) {
      newMinutes = 0;
      newHours += 1;
    }

    if (newHours >= 24) {
      newHours = 0;
    }

    const newTimeStr = `${String(newHours).padStart(2, "0")}:${String(
      newMinutes
    ).padStart(2, "0")}:${String(newSeconds).padStart(2, "0")}`;
    return newTimeStr;
  };

  const handleUpperBlur = (id, value) => {
    setEditing(true);
    const currentItem = between.find((item) => item.id === id);

    if (!currentItem) return;

    const previousUpper = between[between.length - 2]?.upper || ranges[0].upper;

    if (validateTimeInput(value) <= currentItem.lower) {
      setError("Upper bound must be greater than lower bound");
    } else if (validateTimeInput(value) <= previousUpper) {
      setError("Upper bound must be greater than the previous upper bound");
    } else {
      setError("");
      handleBetweenChange(id, "upper", value);
      handleRangeChange(0, "upper", value);
    }
  };

  const handleNextBetween = () => {
    if (error) {
      return;
    }

    const lastUpper =
      between.length > 0 ? between[between.length - 1].upper : ranges[0].upper;

    const newLower = lastUpper;
    const newUpper = incrementTime(newLower);

    setBetween([
      ...between,
      {
        id: Date.now(),
        lower: newLower,
        upper: newUpper,
        score: 0,
      },
    ]);
  };
  const calculateMaxWeight = () => {
    // Get the highest score from lowerScore, upperScore, and all between scores
    const allScores = [
      parseInt(ranges[0]?.lowerScore) || 0,
      parseInt(ranges[0]?.upperScore) || 0,
      ...between.map((item) => parseInt(item.score) || 0),
    ];

    return Math.max(...allScores);
  };

  useEffect(() => {
    if (ranges.length > 0 && item?.fieldId) {
      const range = ranges[0];
      const transformedRange = {
        range: {
          lowerBound: {
            option: range.lower,
            score: Number(range.lowerScore),
          },
          between: between.map((item) => ({
            option: `${item.lower}-${item.upper}`,
            score: Number(item.score),
          })),
          upperBound: {
            option: range.upper,
            score: Number(range.upperScore),
          },
        },
      };

      // Calculate maxWeight based on all the scores
      const maxWeight = calculateMaxWeight();

      // Dispatch the updated range with the correct maxWeight
      dispatch(
        setWeight({
          type: "field",
          fieldId: item?.fieldId,
          scoreType: "byRange",
          range: { ...transformedRange.range },
          maxWeight, // Use the calculated maxWeight
        })
      );
    }
  }, [ranges, between, item?.fieldId, dispatch]);
  useEffect(() => {
    if (type == "form") {
      handleRangeSet(
        `${ranges[0]?.lower} - ${between?.length ? `${between[0]?.lower} - ${between[0]?.upper}` : ""
        }${between?.length ? " - " : ""}${ranges[0]?.upper}`
      );
    }
  }, [ranges, between, type])
  return (
    <Box sx={{ px: type == "form" ? 1 : 5, py: 2, boxSizing: "border-box" }}>
      <Stack spacing={1}>
        {/* Lower Bound */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            type == "form" ? "" : "space-between"
          }
        >
          {
            type == "form" ? null : <InputLabel>Lower Bound</InputLabel>

          }
          <Tooltip title="Less than" placement="top">
            <IconButton>{"<"}</IconButton>
          </Tooltip>
          <TextField
            size="small"
            value={ranges[0]?.lower || ""}
            onChange={(e) => handleRangeChange(0, "lower", e.target.value)}
          />
          
          {
            type == "form" ? null : <>
              <InputLabel>Score</InputLabel>

              <TextField
                size="small"
                value={ranges[0]?.lowerScore || 0}
                onChange={(e) => handleRangeChange(0, "lowerScore", e.target.value)}
              /></>
          }
          {
            type == "form" &&
            <div style={{ marginLeft: "10px" }}>
              <Controller
                name={`evaluation${1}`}
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleChange(
                        `evaluation${1}`,
                        e.target.value
                      );
                    }}
                  >
                    {
                      evaluationOptions?.map((option) => {
                        return <option value={option}>{option}</option>
                      })
                    }
                    {/* <option value="">Select</option>
                  <option value="evaluation1">Evaluation 1</option>
                  <option value="evaluation2">Evaluation 2</option> */}
                  </select>
                )}
              />
            </div>
          }

        </Stack>

        {/* Between Sections */}
        {between.map((item, index) => (
          <Stack
            direction="row"
            position={"relative"}
            spacing={0}
            width={"100%"}
            key={item.id}
            alignItems="center"
            justifyContent={
              type == "form" ? "" : "flex-end"
            }
          >
            {/* <InputLabel>Between</InputLabel> */}
            {
              type == "form" ? null : <InputLabel>Between</InputLabel>

            }
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Tooltip title="Greater than or equal to" placement="top">
                  <IconButton>≥</IconButton>
                </Tooltip>

                {/* Lower Bound (Non-editable) */}
                <TextField size="small" value={item.lower} disabled />
              </div>
              <div>
                <Tooltip title="Less than" placement="top">
                  <IconButton>{"<"}</IconButton>
                </Tooltip>

                <TextField
                  size="small"
                  value={item.upper}
                  onBlur={(e) => handleUpperBlur(item.id, e.target.value)}
                  onChange={(e) =>
                    handleBetweenChange(item.id, "upper", e.target.value)
                  }
                />
              </div>
            </div>
            {
              type == "form" &&
              <div style={{ marginLeft: "10px" }}>
                <Controller
                  name={`evaluation${index + 1 + 1}`}
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handleChange(
                          `evaluation${index + 1 + 1}`,
                          e.target.value
                        );
                      }}
                    >
                      {
                        evaluationOptions?.map((option) => {
                          return <option value={option}>{option}</option>
                        })
                      }
                      {/* <option value="">Select</option>
              <option value="evaluation1">Evaluation 1</option>
              <option value="evaluation2">Evaluation 2</option> */}
                    </select>
                  )}
                />
              </div>
            }

            {
              type == "form" ? null : <>
                <InputLabel>Score</InputLabel>
                <TextField
                  size="small"
                  value={item.score}
                  onChange={(e) =>
                    handleBetweenChange(item.id, "score", e.target.value)
                  }
                />
              </>
            }
            <IconButton
              onClick={() => handleRemoveBetween(item.id)}
              style={{ position: "absolute", top: "30%", right: type == "form" ? "50px" : "-40px" }}
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
          </Stack>
        ))}

        <Box
          display="flex"
          justifyContent="flex-end"
          width={type == "form" ? "90%" : "100%"}
          paddingRight={"60px"}
        >
          <IconButton onClick={handleNextBetween}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Box>

        {/* Upper Bound */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={
            type == "form" ? "" : "space-between"
          }
        >
          {
            type == "form" ? null : <InputLabel>Upper Bound</InputLabel>

          }
          {/* <InputLabel>Upper Bound</InputLabel> */}
          <Tooltip title="Greater than or equal to" placement="top">
            <IconButton>≥</IconButton>
          </Tooltip>
          <TextField
            size="small"
            value={ranges[0]?.upper || ""}
            onChange={(e) => handleRangeChange(0, "upper", e.target.value)}
            disabled
          />
          {
            type == "form" &&
            <div style={{ marginLeft: "10px" }}>
              <Controller
                name={`evaluation${between?.length ? between?.length + 1 + 1 : 2}`}
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleChange(
                        `evaluation${between?.length ? between?.length + 1 + 1 : 2}`,
                        e.target.value
                      );
                    }}
                  >
                    {
                      evaluationOptions?.map((option) => {
                        return <option value={option}>{option}</option>
                      })
                    }
                    {/* <option value="">Select</option>
                    <option value="evaluation1">Evaluation 1</option>
                    <option value="evaluation2">Evaluation 2</option> */}
                  </select>
                )}
              />
            </div>
          }

          {
            type == "form" ? null : <>
              <InputLabel>Score</InputLabel>
              <TextField
                size="small"
                value={ranges[0]?.upperScore || 0}
                onChange={(e) => handleRangeChange(0, "upperScore", e.target.value)}
              /></>
          }
        </Stack>

        {/* Error Message */}
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Stack>
    </Box>
  );
};

export default ScoreRangeComponent;
